import React, {useReducer, useContext} from "react";

import Toggles from './toggles';
import {cssClasses, identity} from '../shared/utils';

const VALID = Symbol('valid');
const INVALID = Symbol('invalid');
const UNVALIDATED = Symbol('unvalidated');

const _VALIDATION_RESULTS = new Set([INVALID, VALID]);

function validationResult(valid) {
	if (valid === UNVALIDATED) {
		return valid;
	}
	if (_VALIDATION_RESULTS.has(valid)) {
		return valid;
	}

	return valid ? VALID : INVALID;
}


class FormValue {
	constructor(value, valid = UNVALIDATED) {
		this.value = value;
		this.valid = valid;
	}

	isSame(value, valid) {
		value = FormValue.fromValue(value, valid);
		return Object.is(this.value, value.value) && (this.valid === value.valid);
	}

	isValid() {
		return this.valid === VALID;
	}

	isValidated() {
		return this.valid !== UNVALIDATED;
	}

	hasError() {
		return this.valid !== UNVALIDATED && this.valid === INVALID;
	}

	static validate(value, validate) {
		if (validate instanceof Function) {
			try {
				let r = validate(value);
				if (r !== undefined) {
					value = r;
				}
				return new FormValue(r, VALID);
			} catch (err) {
				return new FormValue(value, INVALID);
			}
		}

		return new FormValue(value, UNVALIDATED);
	}

	static fromValue(value, valid = UNVALIDATED) {
		if (value instanceof FormValue) {
			return value;
		} else {
			valid = validationResult(valid);
			return new FormValue(value, valid);
		}
	}
}

function _hasPath(obj, p) {
	return Boolean(obj) && obj.constructor === Object && obj.hasOwnProperty(p);
}

function _makePath(path) {
	return Array.isArray(path) ? path : [path];
}

function _setValue(obj, path, value) {
	let o = obj;
	for (let k of path.slice(0, -1)) {
		if (o.hasOwnProperty(k)) {
			o = o[k];
			if (o.constructor !== Object) {
				throw new Error(`Path conflict`);
			}
		} else {
			o[k] = {};
			o = o[k];
		}
	}

	let k = path[path.length - 1];
	o[k] = value;
	return obj;
}

function _setFormValue(obj, path, value, valid) {
	if (!(value instanceof FormValue)) {
		value = new FormValue(value, valid);
	}
	return _setValue(obj, path, value);
}

function * _paths(obj) {
	if (!obj || obj.constructor !== Object) {
		return;
	}
	let queue = Object.entries(obj).map(([k, v]) => [[k], v]);
	while (queue.length) {
		let [p, v] = queue.shift();
		if (v && v.constructor === Object) {
			queue = Object.keys(v).map(k => [[...p, k], v[k]]).concat(queue);
		} else {
			yield [p, v];
		}
	}
}

function _setValues(obj, src) {
	if (!src || src.constructor !== Object) {
		return obj;
	}
	for (let [p, v] of _paths(src)) {
		_setFormValue(obj, p, v, UNVALIDATED);
	}
	return obj;
}

class FormState {
	constructor(name, ...values) {
		this.name = name;
		this.values = values.reduce(_setValues, {});
	}

	_getFormValue(path) {
		path = _makePath(path);
		let values = this.values;
		for (let k of path.slice(0, -1)) {
			if (_hasPath(values, k)) {
				values = values[k];
			} else {
				return null;
			}
		}
		let k = path[path.length - 1];
		if (_hasPath(values, k)) {
			return values[k];
		} else {
			return null;
		}
	}

	getValue(path, def = '') {
		let formValue = this._getFormValue(path);
		if (formValue !== null) {
			return formValue.value;
		} else {
			return def;
		}
	}

	getValues() {
		let values = {};
		for (let [p, v] of _paths(this.values)) {
			_setValue(values, p, v.value);
		}
		return values;
	}

	hasError(path) {
		let value = this._getFormValue(path);
		return value && value.hasError();
	}

	static setValue(state, path, value, valid = UNVALIDATED) {
		value = FormValue.fromValue(value, valid);
		path = _makePath(path);
		let currentValue = state._getFormValue(path);
		if (currentValue && currentValue.isSame(value)) {
			return state;
		}
		return new FormState(state.name, state.values, _setValue({}, path, value));
	}

	static reducer(state, action) {
		let [method, ...args] = action;
		if (FormState.ValidActions.has(method)) {
			return FormState[method](state, ...args);
		} else {
			return state;
		}
	}
}

Object.defineProperty(FormState, 'ValidActions', {value: new Set(['setValue'])});

const FormContext = React.createContext(null);

function initForm(name) {
	function Form(props) {
		let {values = {}, className, onSubmit} = props;
		const [formState, dispatch] = useReducer(
			FormState.reducer,
			new FormState(name, values)
		);

		return (
			<FormContext.Provider value={{state: formState, dispatch}}>
				<form
					name={name} className={className}
					onSubmit={
						(e) => {
							e.preventDefault();
							if (onSubmit instanceof Function) {
								onSubmit(e, formState);
							}
						}
					}
				>
					{props.children}
				</form>
			</FormContext.Provider>
		);
	}

	function useFormState() {
		let {state, dispatch} = useContext(FormContext);
		let handierDispatch = (...args) => dispatch(Array.from(args));
		return [state, handierDispatch];
	}

	function useFormControlProps(props) {
		let [state, dispatch] = useFormState();
		let {path, onChange, validate} = props;
		let controlProps = Object.assign({}, props, {
			value: state.getValue(path),
			onChange: (e) => {
				if (onChange instanceof Function) {
					onChange(e);
				}
				let v = e.target.value;
				v = FormValue.validate(v, validate);
				dispatch(`setValue`, path, v);
			}
		});

		// Omit children, so it can go like <input ...useFormControlProps(props)>
		delete controlProps.children;

		// Omit validate cause so it's not passed to the input
		delete controlProps.validate;

		return controlProps;
	}

	function FormGroup(props) {
		let [state] = useFormState();
		let {htmlFor, children, className = 'form-group', label = null} = props;
		let hasError = htmlFor && state.hasError(htmlFor);
		if (label) {
			label = <label htmlFor={htmlFor}>{label}</label>
		} else {
			label = null;
		}
		return (
			<div className={cssClasses(className, {'has-error': hasError})}>
				{label}
				{children}
			</div>
		);
	}

	function Input(props) {
		return <input {...useFormControlProps(props)} />;
	}

	function Display(props) {
		let {label, path, defaultValue, className, formatter} = props;
		let [state] = useFormState();
		let value = state.getValue(path);
		if (formatter instanceof Function) {
			value = formatter(value) || defaultValue || null;
		}
		return (
			<div className={cssClasses('cm-display', className)}>
				<span className="cm-label">{label}</span>
				{' '};
				<span className="cm-value">{value}</span>
			</div>
		);
	}

	function Select(props) {
		let {children} = props;
		return (
			<select {...useFormControlProps(props)}>
				{children}
			</select>
		);
	}

	function TogglesSelect(props) {
		// Can't use useFormControlProps because onChange isn't an event listener here,
		// and i can't make it this way because it's not react-y (╯°□°)╯︵ ┻━┻
		let [state, dispatch] = useFormState();
		let {path, onChange} = props;
		let controlProps = {
			value: state.getValue(path),
			onChange: (toggles) => {
				if (onChange instanceof Function) {
					onChange(toggles);
				}
				dispatch(`setValue`, path, [...toggles.toggledOn()]);
			}
		};
		return <Toggles {...props} {...controlProps} />;
	}

	return {Form, Input, Display, FormGroup, Select, TogglesSelect, useFormState, useFormControlProps};
}

export default initForm;
export {FormValue};
